import React from "react";

export function PageHeader({ title, subtitle, action }) {
    return (
        <div className="flex flex-col sm:flex-row sm:items-end justify-between gap-4 pb-6 border-b border-slate-200 mb-6">
            <div>
                <h1 className="font-display text-3xl sm:text-4xl font-black tracking-tight text-slate-900">{title}</h1>
                {subtitle && <p className="text-sm text-slate-500 mt-1">{subtitle}</p>}
            </div>
            {action && <div>{action}</div>}
        </div>
    );
}

export function Card({ children, className = "" }) {
    return <div className={`bg-white border border-slate-200 rounded-xl ${className}`}>{children}</div>;
}

export function CardBody({ children, className = "" }) {
    return <div className={`p-6 ${className}`}>{children}</div>;
}

export function Stat({ label, value, accent = "orange", tid }) {
    const accents = {
        orange: "from-orange-500 to-orange-600",
        slate: "from-slate-700 to-slate-900",
        emerald: "from-emerald-500 to-emerald-600",
        blue: "from-blue-500 to-blue-600",
    };
    return (
        <div data-testid={tid} className="bg-white border border-slate-200 rounded-xl p-6 flex flex-col gap-2 hover:shadow-sm transition">
            <div className="text-[10px] uppercase tracking-widest text-slate-500 font-bold">{label}</div>
            <div className={`font-display text-3xl font-black bg-gradient-to-br ${accents[accent]} bg-clip-text text-transparent`}>{value}</div>
        </div>
    );
}

export function Badge({ children, color = "slate" }) {
    const map = {
        slate: "bg-slate-100 text-slate-700",
        emerald: "bg-emerald-100 text-emerald-700",
        amber: "bg-amber-100 text-amber-700",
        red: "bg-red-100 text-red-700",
        orange: "bg-orange-100 text-orange-700",
        blue: "bg-blue-100 text-blue-700",
    };
    return <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-semibold ${map[color]}`}>{children}</span>;
}

export function Button({ variant = "primary", className = "", ...props }) {
    const styles = {
        primary: "bg-orange-500 hover:bg-orange-600 text-white",
        secondary: "bg-slate-100 hover:bg-slate-200 text-slate-900",
        outline: "border border-slate-200 hover:border-slate-300 text-slate-700 bg-white",
        danger: "bg-red-500 hover:bg-red-600 text-white",
    };
    return (
        <button
            className={`inline-flex items-center justify-center gap-2 font-semibold rounded-lg px-4 py-2 text-sm transition-colors disabled:opacity-50 disabled:cursor-not-allowed ${styles[variant]} ${className}`}
            {...props}
        />
    );
}

export function Input({ label, error, className = "", ...props }) {
    return (
        <label className="block">
            {label && <span className="text-xs font-semibold text-slate-700 mb-1 block uppercase tracking-wider">{label}</span>}
            <input
                className={`w-full rounded-lg border border-slate-200 px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-orange-500 focus:border-orange-500 bg-white ${className}`}
                {...props}
            />
            {error && <span className="text-xs text-red-600 mt-1 block">{error}</span>}
        </label>
    );
}

export function Select({ label, children, className = "", ...props }) {
    return (
        <label className="block">
            {label && <span className="text-xs font-semibold text-slate-700 mb-1 block uppercase tracking-wider">{label}</span>}
            <select
                className={`w-full rounded-lg border border-slate-200 px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-orange-500 bg-white ${className}`}
                {...props}
            >
                {children}
            </select>
        </label>
    );
}

export function Textarea({ label, className = "", ...props }) {
    return (
        <label className="block">
            {label && <span className="text-xs font-semibold text-slate-700 mb-1 block uppercase tracking-wider">{label}</span>}
            <textarea
                className={`w-full rounded-lg border border-slate-200 px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-orange-500 bg-white ${className}`}
                {...props}
            />
        </label>
    );
}

export function Empty({ title, hint }) {
    return (
        <div className="text-center py-16 bg-white border border-dashed border-slate-200 rounded-xl">
            <h3 className="font-display text-lg font-bold text-slate-700">{title}</h3>
            {hint && <p className="text-sm text-slate-500 mt-1">{hint}</p>}
        </div>
    );
}
